import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Eye, EyeSlash, Wallet, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";

const ROLES = [
  { value: "admin", label: "Administrator" },
  { value: "manager", label: "Manager" },
  { value: "pengurus", label: "Pengurus" },
  { value: "korwil", label: "KORWIL" },
  { value: "korsub", label: "KORSUB" },
  { value: "asisten_supervisor", label: "Asisten Supervisor" },
];

const EXPORT_COLS = [
  { header: "Nama", accessor: (r) => r.name },
  { header: "Email", accessor: (r) => r.email },
  { header: "Peran", accessor: (r) => ROLES.find(rl => rl.value === r.role)?.label || r.role },
  { header: "Wilayah", accessor: (r) => r.wilayah || "-" },
  { header: "Blok", accessor: (r) => r.blok || "-" },
  { header: "Simpanan", accessor: (r) => r.total_simpanan || 0 },
  { header: "Pinjaman", accessor: (r) => r.total_pinjaman || 0 },
  { header: "Status", accessor: (r) => r.is_active !== false ? "Aktif" : "Nonaktif" },
];

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [financeDialog, setFinanceDialog] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "asisten_supervisor",
    phone: "", wilayah: "", blok: "",
    can_manage_users: false,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/users?page=${page}&limit=15&search=${search}`);
      setUsers(data.data);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const update = { ...form };
        if (!update.password) delete update.password;
        await api.put(`/users/${editUser._id}`, update);
        toast.success("Pengguna berhasil diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Pengguna berhasil ditambahkan");
      }
      setDialogOpen(false);
      setEditUser(null);
      setShowPw(false);
      setForm({ email: "", password: "", name: "", role: "asisten_supervisor", phone: "", wilayah: "", blok: "", can_manage_users: false });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setShowPw(false);
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      phone: u.phone || "", wilayah: u.wilayah || "", blok: u.blok || "",
      can_manage_users: u.can_manage_users || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus pengguna ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Pengguna dihapus"); load(); } catch { toast.error("Gagal menghapus"); }
  };

  const openFinance = async (u) => {
    setFinanceDialog(u);
    try {
      const { data } = await api.get(`/users/${u._id}/finance`);
      setFinanceData(data);
    } catch { setFinanceData({ simpanan: [], pinjaman: [], total_simpanan: 0, total_pinjaman: 0, saldo_bersih: 0 }); }
  };

  const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  const roleColors = { admin: "bg-purple-100 text-purple-700", manager: "bg-blue-100 text-blue-700", pengurus: "bg-cyan-100 text-cyan-700", korwil: "bg-emerald-100 text-emerald-700", korsub: "bg-amber-100 text-amber-700", asisten_supervisor: "bg-slate-100 text-slate-700" };

  return (
    <div data-testid="user-management-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Manajemen Pengguna</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola akun, peran, dan keuangan anggota</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons onExcel={() => exportToExcel(users, EXPORT_COLS, "pengguna")} onPDF={() => exportToPDF(users, EXPORT_COLS, "pengguna", "Data Pengguna P3UW")} />
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditUser(null); setShowPw(false); } }}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Tambah Pengguna</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>{editUser ? "Edit Pengguna" : "Pengguna Baru"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Nama</Label><Input data-testid="input-user-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Email</Label><Input data-testid="input-user-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>

                {/* Password with eye toggle */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">
                    {editUser ? "Password (kosongkan jika tidak diubah)" : "Password"}
                  </Label>
                  <div className="relative">
                    <Input
                      data-testid="input-user-password"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      required={!editUser}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Peran</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}><SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Telepon</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Wilayah</Label><Input value={form.wilayah} onChange={e => setForm({ ...form, wilayah: e.target.value })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Blok</Label><Input value={form.blok} onChange={e => setForm({ ...form, blok: e.target.value })} /></div>
                </div>

                {/* Permission: can manage users */}
                {currentUser?.role === "admin" && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-[#B8860B]/5 border border-[#B8860B]/10">
                    <Checkbox
                      id="can_manage_users"
                      checked={form.can_manage_users}
                      onCheckedChange={(v) => setForm({ ...form, can_manage_users: !!v })}
                      data-testid="checkbox-can-manage-users"
                    />
                    <div>
                      <Label htmlFor="can_manage_users" className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
                        Izinkan membuat akun pengguna
                      </Label>
                      <p className="text-[10px] text-slate-500 mt-0.5">Pengguna ini dapat menambah & mengelola akun pengguna lain</p>
                    </div>
                  </div>
                )}

                <Button type="submit" data-testid="submit-user-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative w-full sm:w-72">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-users" placeholder="Cari pengguna..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Nama</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Peran</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Wilayah</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-center">Hak Kelola</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Tidak ada pengguna</TableCell></TableRow>
              ) : users.map(u => (
                <TableRow key={u._id} className="hover:bg-slate-50">
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><Badge className={`${roleColors[u.role] || ""} hover:${roleColors[u.role] || ""}`}>{ROLES.find(r => r.value === u.role)?.label || u.role}</Badge></TableCell>
                  <TableCell className="text-sm">{u.wilayah || "-"}{u.blok ? ` / ${u.blok}` : ""}</TableCell>
                  <TableCell className="text-center">
                    {(u.role === "admin" || u.can_manage_users) ? (
                      <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px]">Kelola User</Badge>
                    ) : <span className="text-slate-300 text-xs">-</span>}
                  </TableCell>
                  <TableCell><Badge className={u.is_active !== false ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{u.is_active !== false ? "Aktif" : "Nonaktif"}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openFinance(u)} title="Keuangan Anggota" className="text-[#008080]"><Wallet size={16} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(u)}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(u._id)}><Trash size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>

      {/* Finance dialog per user */}
      <Dialog open={!!financeDialog} onOpenChange={() => { setFinanceDialog(null); setFinanceData(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Keuangan: {financeDialog?.name}</DialogTitle></DialogHeader>
          {financeData ? (
            <div className="space-y-4 mt-3">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-600">Simpanan</p>
                  <p className="text-base font-bold text-emerald-700" style={{ fontFamily: "Work Sans" }}>{fmt(financeData.total_simpanan)}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-red-600">Pinjaman</p>
                  <p className="text-base font-bold text-red-700" style={{ fontFamily: "Work Sans" }}>{fmt(financeData.total_pinjaman)}</p>
                </div>
                <div className={`p-3 rounded-lg border text-center ${financeData.saldo_bersih >= 0 ? "bg-[#B8860B]/5 border-[#B8860B]/20" : "bg-red-50 border-red-200"}`}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Saldo Bersih</p>
                  <p className={`text-base font-bold ${financeData.saldo_bersih >= 0 ? "text-[#B8860B]" : "text-red-700"}`} style={{ fontFamily: "Work Sans" }}>{fmt(financeData.saldo_bersih)}</p>
                </div>
              </div>

              {/* Simpanan list */}
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Riwayat Simpanan</p>
                {financeData.simpanan.length > 0 ? (
                  <div className="space-y-1.5">
                    {financeData.simpanan.map((s, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5 px-2 border-b border-slate-50">
                        <span className="text-slate-600">{s.tanggal} — <Badge className="text-[9px] bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{s.tipe}</Badge></span>
                        <span className="font-medium text-emerald-700">{fmt(s.jumlah)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400 py-2">Belum ada simpanan</p>}
              </div>

              {/* Pinjaman list */}
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Riwayat Pinjaman</p>
                {financeData.pinjaman.length > 0 ? (
                  <div className="space-y-1.5">
                    {financeData.pinjaman.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5 px-2 border-b border-slate-50">
                        <span className="text-slate-600">{p.tanggal} — <Badge className={`text-[9px] ${p.status === "lunas" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} hover:bg-inherit`}>{p.status}</Badge></span>
                        <span className="font-medium text-red-600">{fmt(p.jumlah)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400 py-2">Belum ada pinjaman</p>}
              </div>
            </div>
          ) : <p className="text-center py-8 text-slate-400">Memuat data keuangan...</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
